import {
  useFormContext,
  Controller,
  UseControllerProps,
  RegisterOptions,
  FieldValues,
  FieldError,
} from "react-hook-form";
import { Rifm } from "rifm";
import { Form, FormItemProps, Input, InputProps, Typography } from "antd";
import { useContext } from "react";
import { ToastContext } from "../../context/toast/incredable-toast";

// ----------------------------------------------------------------------

type RFimProps = {
  accept?: RegExp;
  format?: (v: string) => string;
  replace?: (str: string) => string;
};

type PasswordInputProps = InputProps & {
  type: "password";
  visibilityToggle?: boolean | { visible: boolean; onVisibleChange: (visible: boolean) => void };
  iconRender?: (visible: boolean) => React.ReactNode;
};

type RegularInputProps = Omit<InputProps, "type"> & {
  type?: Exclude<InputProps["type"], "password">;
};

type Props = {
  formItemProps: FormItemProps;
  inputProps: PasswordInputProps | RegularInputProps;
  readonly?: boolean;
  rfim?: RFimProps;
  controllerProps: UseControllerProps;
  rules?:
  | Omit<
    RegisterOptions<FieldValues, string>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >
  | undefined;
  canToggleVisibility?: boolean;
};

export default function RHFTextField({
  formItemProps,
  inputProps,
  readonly,
  rfim,
  controllerProps,
  rules,
  canToggleVisibility,
}: Props) {
  const { control } = useFormContext();
  const { Text } = Typography;
  const toastContext = useContext(ToastContext);
  return (
    <Controller
      name={controllerProps.name}
      control={control}
      defaultValue={controllerProps.defaultValue}
      render={({ field, fieldState }) => {
        return (
          <Form.Item
            {...formItemProps}
            label={
              <span style={{ fontWeight: "600" }}>
                {formItemProps?.label}{" "}
                {!!rules?.required ? <Text type="danger">*</Text> : <></>}
              </span>
            }
            // required={!!rules?.required}
            validateStatus={!!fieldState.error ? "error" : ""}
            help={!!fieldState.error && getErrorMessage(fieldState.error)}
          >
            <Rifm
              accept={rfim?.accept}
              format={rfim?.format ?? defaultFormat}
              replace={rfim?.replace}
              value={field.value}
              onChange={(str) => field.onChange(str)}
            >
              {({ value: rifm_value }) => {
                if (inputProps.type === "password") {
                  const passwordProps = inputProps as PasswordInputProps;
                  return (
                    <Input.Password
                      {...passwordProps}
                      style={{
                        borderRadius: "6px",
                        boxShadow: "0px 1px 2px 0px #1018280A",
                        ...passwordProps.style,
                      }}
                      value={rifm_value}
                      onChange={(e) => field.onChange(e.target.value)}
                      visibilityToggle={
                        canToggleVisibility === false
                          ? false
                          : passwordProps.visibilityToggle ?? true
                      }
                      onClick={(e) => {
                        if (!canToggleVisibility) {
                          e.preventDefault();
                          toastContext?.openErrorNotification({
                            message: "You don't have permission to view or edit the value.",
                            placement: "topRight",
                          });
                        }
                      }}
                    />
                  );
                } else {
                  const regularProps = inputProps as RegularInputProps;
                  return (
                    <Input
                      {...regularProps}
                      style={{
                        borderRadius: "6px",
                        boxShadow: "0px 1px 2px 0px #1018280A",
                        ...regularProps.style,
                      }}
                      readOnly={readonly}
                      value={rifm_value}
                      onChange={(changeDetail) => {
                        field.onChange(changeDetail.target.value);
                      }}
                    />
                  );
                }
              }}
            </Rifm>
          </Form.Item>
        );
      }}
      rules={{ maxLength: 256, ...rules }}
    />
  );
}

const getErrorMessage = (error?: FieldError | undefined) => {
  console.log({ error });
  if (!error) return;
  if (error.message && error.message.length > 0) return error.message;
  if (error.type === "maxLength") return "Value is too long";
  return "Invalid value";
};

const defaultFormat = (v: string) => v;
